/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyReturnKey,
  ShopifyReturnLineItemKey,
  ShopifyReturnLineItemToReturnKey,
  ShopifyReturnLineItemToReturnReasonDefinitionKey,
  ShopifyReturnLineItemToShopKey,
  ShopifyReturnReasonDefinitionKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// Hardcoded field keys to preserve deterministic keys when shopifyFieldKey was added
const customerNoteFieldKey = "ModelField-DataModel-Shopify-ReturnLineItem-customerNote";
const customerNoteStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-ReturnLineItem-customerNote-initial";
const returnReasonFieldKey = "ModelField-DataModel-Shopify-ReturnLineItem-returnReason";
const returnReasonStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-ReturnLineItem-returnReason-initial";
const returnReasonNoteFieldKey = "ModelField-DataModel-Shopify-ReturnLineItem-returnReasonNote";
const returnReasonNoteStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-ReturnLineItem-returnReasonNote-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReturnLineItemKey,
  "2024-07": {
    name: "Shopify Return Line Item",
    fields: [
      {
        name: "Customer Note",
        fieldType: FieldType.String,
        key: customerNoteFieldKey,
        initialStorageEpochKey: customerNoteStorageEpochKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "customer_note", graphQLFieldKey: "customerNote" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity", graphQLFieldKey: "quantity" } },
      },
      {
        name: "Refundable Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "refundableQuantity" } },
      },
      {
        name: "Refunded Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "refundedQuantity" } },
      },
      {
        name: "Return Reason",
        fieldType: FieldType.Enum,
        key: returnReasonFieldKey,
        initialStorageEpochKey: returnReasonStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "return_reason", graphQLFieldKey: "returnReason", isShopifyEnum: true },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "COLOR" },
            { name: "DEFECTIVE" },
            { name: "NOT_AS_DESCRIBED" },
            { name: "OTHER" },
            { name: "SIZE_TOO_LARGE" },
            { name: "SIZE_TOO_SMALL" },
            { name: "STYLE" },
            { name: "UNKNOWN" },
            { name: "UNWANTED" },
            { name: "WRONG_ITEM" },
          ],
        },
      },
      {
        name: "Return Reason Note",
        fieldType: FieldType.String,
        key: returnReasonNoteFieldKey,
        initialStorageEpochKey: returnReasonNoteStorageEpochKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "return_reason_note", graphQLFieldKey: "returnReasonNote" } },
      },
      {
        name: "Return",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnLineItemToReturnKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "return_id", graphQLFieldKey: "returnId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyReturnKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnLineItemToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.returns",
      resource: "return.returnLineItems",
      additionalFields: ["returnId:id"],
    },
  },
  "2026-01": {
    fields: [
      {
        name: "Return Reason",
        fieldType: FieldType.Enum,
        key: returnReasonFieldKey,
        initialStorageEpochKey: returnReasonStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "return_reason",
            graphQLFieldKey: "returnReason",
            isShopifyEnum: true,
            deprecated: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [
            { name: "COLOR" },
            { name: "DEFECTIVE" },
            { name: "NOT_AS_DESCRIBED" },
            { name: "OTHER" },
            { name: "SIZE_TOO_LARGE" },
            { name: "SIZE_TOO_SMALL" },
            { name: "STYLE" },
            { name: "UNKNOWN" },
            { name: "UNWANTED" },
            { name: "WRONG_ITEM" },
          ],
        },
      },
      {
        name: "Return Reason Definition",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReturnLineItemToReturnReasonDefinitionKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "returnReasonDefinition",
            graphQLSelection: "id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyReturnReasonDefinitionKey,
        },
      },
    ],
  },
};

export const ShopifyReturnLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
